import React, { useContext, useState } from 'react';
import { Box, Card, CardContent, Checkbox, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import PropTypes from 'prop-types';
import { FileIcon, defaultStyles } from 'react-file-icon';
import imagesAPI from '../components/images';
import { ContextStore } from '../store/contextStore';

const useStyles = makeStyles(() => ({
    card: {
        cursor: 'pointer',
        borderRadius: '12px',
        border: '1px solid #D8D8D8',
        backgroundColor: '#F9FAFB',
        transition: 'all 0.2s ease',
        width: '203px',
        boxShadow: 'none !important',
        '&:hover': {
            borderColor: '#007AFF',
        },
    },
    cardSelected: {
        borderColor: '#007AFF',
        backgroundColor: '#EAF4FF',
    },
    previewWrapper: {
        padding: '12px 12px 0 12px',
        position: 'relative',
    },
    checkboxWrapper: {
        position: 'absolute',
        top: 12,
        right: 12,
        zIndex: 1,
    },
    mediaPreview: {
        width: '179px',
        height: '179px',
        borderRadius: '4px',
        overflow: 'hidden',
    },
    mediaImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    fileIconContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        backgroundColor: '#F5F5F5',
    },
    fileIconWrapper: {
        width: '80px',
        height: '100px',
    },
    content: {
        padding: '12px !important',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    filename: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#000000',
        lineHeight: '1.4em',
        minHeight: '2.8em',
    },
    metadata: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '12px',
        color: '#667085',
    },
    divider: {
        width: '0',
        height: '10px',
        borderLeft: '1px solid #667085',
    },
    sender: {
        display: 'block',
        fontSize: '12px',
        color: '#667085',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
}));

const MediaCard = React.memo(({ media, index, selected = false, onSelect }) => {
    const classes = useStyles();
    const { settings } = useContext(ContextStore);
    const [imageError, setImageError] = useState(false);

    const getThumbnailUrl = () => {
        // For videos, we do not use the image service
        if (media.file_type === 'video') {
            return null;
        }
        if (media.image && media.image.p && media.image.s) {
            return imagesAPI.url(settings.images.base, media.image, 'cover_179x179');
        }
        return media.media_url_resolved;
    };

    const handleClick = () => {
        // For videos and other non-images, we download the file
        if (media.file_type === 'video') {
            const link = document.createElement('a');
            link.href = media.media_url_resolved;
            link.download = getFilename();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return;
        }
        window.open(media.media_url_resolved, '_blank');
    };

    const handleCheckboxClick = (e) => {
        e.stopPropagation();
        if (onSelect) {
            onSelect(media.id, index, e);
        }
    };

    const formatDate = (dateStr) => {
        return moment(dateStr).format('MMM DD, YYYY');
    };

    const formatTime = (dateStr) => {
        return moment(dateStr).format('hh:mm A');
    };

    const formatSenderName = () => {
        const fromSender = media.sender?.display_name;
        const fromDisplay = media.sender_display_name || media.display_name;
        if (fromSender) return fromSender;
        if (fromDisplay) return fromDisplay;
        const first = media.sender?.first_name || media.sender_first_name || media.first_name;
        const last = media.sender?.last_name || media.sender_last_name || media.last_name;
        const combined = [first, last].filter(Boolean).join(' ').trim();
        if (combined) return combined;
        return media.sender_name || 'Unknown';
    };

    const formatSenderEmail = () =>
        media.sender?.email ||
        media.sender_email ||
        media.email ||
        media.sender_user_email ||
        media.user_email ||
        '';

    const getFilename = () => {
        if (media.media_url_resolved) {
            const parts = media.media_url_resolved.split('/');
            return parts[parts.length - 1].split('?')[0];
        }
        return `${media.sender_name}_${media.id}`;
    };

    const getFileExtension = () => {
        const url = media.thumbnail_url || media.media_url_resolved || '';
        const filename = url.split('/').pop().split('?')[0];
        const parts = filename.split('.');
        if (parts.length > 1) {
            return parts.pop().toLowerCase();
        }
        return 'file';
    };



    const getSenderRole = () => {
        if (media.sender_role === 'GUEST') return 'Guest';
        if (media.sender?.is_guide) return 'Guide';
        if (media.sender_role) {
            return media.sender_role.charAt(0).toUpperCase() + media.sender_role.slice(1).toLowerCase();
        }
        return '';
    };

    const getSenderInfo = () => {
        const name = formatSenderName();
        const email = formatSenderEmail();
        const role = getSenderRole();

        if (email && name !== email) {
            return `${name} (${role}) • ${email}`;
        }
        return `${name} (${role})`;
    };

    const renderPreview = () => {
        const ext = getFileExtension();
        const thumbnailUrl = getThumbnailUrl();

        // Для видео или при ошибке загрузки - показываем иконку файла
        if (media.file_type === 'video' || imageError || !thumbnailUrl) {
            return (
                <Box className={classes.fileIconContainer}>
                    <Box className={classes.fileIconWrapper}>
                        <FileIcon
                            extension={ext || 'file'}
                            {...defaultStyles[ext || 'file']}
                        />
                    </Box>
                </Box>
            );
        }

        return (
            <img
                src={thumbnailUrl}
                alt=""
                className={classes.mediaImage}
                loading="lazy"
                decoding="async"
                onError={() => setImageError(true)}
            />
        );
    };

    return (
        <Card
            className={`${classes.card} ${selected ? classes.cardSelected : ''}`}
            onClick={handleClick}
        >
            <Box className={classes.previewWrapper}>
                {onSelect && (
                    <Box className={classes.checkboxWrapper}>
                        <Checkbox
                            checked={selected}
                            onClick={handleCheckboxClick}
                            sx={{
                                color: 'white',
                                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                borderRadius: '4px',
                                '&.Mui-checked': {
                                    color: '#007AFF',
                                    backgroundColor: 'white',
                                },
                            }}
                        />
                    </Box>
                )}
                <Box className={classes.mediaPreview}>
                    {renderPreview()}
                </Box>
            </Box>
            <CardContent className={classes.content}>
                <Tooltip title={getFilename()} placement="top">
                    <Box
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            wordBreak: 'break-word',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#000000',
                            lineHeight: '1.4em',
                            minHeight: '2.8em',
                        }}
                    >
                        {getFilename()}
                    </Box>
                </Tooltip>
                <Box className={classes.metadata}>
                    <span>{formatDate(media.timestamp)}</span>
                    <Box className={classes.divider} />
                    <span>{formatTime(media.timestamp)}</span>
                </Box>
                <Tooltip title={getSenderInfo()} placement="bottom">
                    <span className={classes.sender}>
                        {getSenderInfo()}
                    </span>
                </Tooltip>
            </CardContent>
        </Card>
    );
});

MediaCard.displayName = 'MediaCard';

MediaCard.propTypes = {
    media: PropTypes.shape({
        id: PropTypes.number.isRequired,
        media_url_resolved: PropTypes.string,
        image: PropTypes.shape({
            p: PropTypes.string,
            s: PropTypes.string,
        }),
        thumbnail_url: PropTypes.string,
        file_type: PropTypes.string,
        timestamp: PropTypes.string,
        sender: PropTypes.shape({
            display_name: PropTypes.string,
            first_name: PropTypes.string,
            last_name: PropTypes.string,
            email: PropTypes.string,
            is_guide: PropTypes.bool,
        }),
        sender_display_name: PropTypes.string,
        display_name: PropTypes.string,
        sender_first_name: PropTypes.string,
        first_name: PropTypes.string,
        sender_last_name: PropTypes.string,
        last_name: PropTypes.string,
        sender_name: PropTypes.string,
        sender_email: PropTypes.string,
        email: PropTypes.string,
        sender_user_email: PropTypes.string,
        user_email: PropTypes.string,
        sender_role: PropTypes.string,
    }).isRequired,
    index: PropTypes.number.isRequired,
    selected: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
};

export default MediaCard;

